import { useDialogModal } from "@/hook/handle-modal/hooks/actions"
import { useSoftDeleteDivergenceMutation } from "@/services/divergence"
import { Button } from "components/ui/button"
import { toast } from "components/ui/use-toast"
import { AlertTriangle, Trash2 } from "lucide-react"

interface DeleteDivergenceModalProps {
  id: number
  version: number
  name: string
}

export const DeleteDivergenceModal = ({ id, version, name }: DeleteDivergenceModalProps) => {
  const [softDeleteDivergence, { isLoading }] = useSoftDeleteDivergenceMutation()
  const { handleModal } = useDialogModal()

  const handleDelete = async () => {
    try {
      await softDeleteDivergence({
        id,
        version,
      }).unwrap()

      toast({
        variant: "default",
        description: "Divergência excluída com sucesso",
        duration: 5000,
      })

      handleModal({
        isOpen: false,
        element: null,
        title: "",
        description: "",
      })
    } catch (error) {
      console.error("Erro ao excluir divergência:", error)
      toast({
        variant: "destructive",
        description: "Erro ao excluir divergência. Tente novamente.",
        duration: 5000,
      })
    }
  }

  const handleCancel = () => {
    handleModal({
      isOpen: false,
      element: null,
      title: "",
      description: "",
    })
  }

  return (
    <div className="space-y-6 p-1">
      {/* Warning Icon and Message */}
      <div className="flex items-start gap-4">
        <div className="flex-shrink-0">
          <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
        </div>
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">
            Confirmar Exclusão
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">
            Tem certeza que deseja excluir a divergência <strong>"{name}"</strong>?
          </p>
          <p className="text-xs text-gray-500 mt-2">
            Esta ação não pode ser desfeita. A divergência será marcada como excluída.
          </p>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          variant="outline"
          onClick={handleCancel}
          disabled={isLoading}
          className="flex-1"
        >
          Cancelar
        </Button>
        <Button
          variant="destructive"
          onClick={handleDelete}
          disabled={isLoading}
          className="flex-1"
        >
          {isLoading ? (
            <>
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
              Excluindo...
            </>
          ) : (
            <>
              <Trash2 className="w-4 h-4 mr-2" />
              Excluir Divergência
            </>
          )}
        </Button>
      </div>
    </div>
  )
}
